import React, { useContext, useEffect, useState, useCallback, useRef } from 'react';
import { IconButton, Link, makeStyles } from '@material-ui/core';
import { ReactComponent as UpdateIcon } from 'assets/icons/update.svg';
import Modal from './components/Shared/Modal';
import { ALL_ROUTES } from './config/routes';
import { AppRouteContext } from './contexts';
import { useDispatch, useSelector } from 'react-redux';
import { updateIsMaintenanceMode } from './redux_store/reducer/reducers/userReducer/userReducer';
import { checkIsMaintenanceMode } from './helpers/downForMaintenance';

const useStyles = makeStyles((theme) => ({
  modal: {
    alignItems: 'center',
    paddingBottom: theme.spacing(7),
    width: '100%',
    maxWidth: '766px',
  },
  skipNow: {
    color: theme.colors.lightBlue,
    cursor: 'pointer',
    fontSize: theme.spacing(2),
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',

    '&:hover': {
      textDecoration: 'none',
    },
  },
  label: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '1rem',
  },
  iconButton: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    margin: theme.spacing(4),
    width: 'max-content',
    borderRadius: theme.spacing(1),
    color: theme.colors.blue2,
    fontSize: theme.spacing(3),
    fontWeight: 600,
  },
}));

// Constants
const CHECK_VERSION_INTERVAL = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
const API_TIMEOUT = 10000; // 10 seconds

const CacheBuster = ({ children }) => {
  const classes = useStyles();
  const { en: { dialogs } } = useSelector((state) => state.i18n);
  const dispatch = useDispatch();
  const { setAppRoute } = useContext(AppRouteContext);
  const { isMaintenanceMode } = useSelector((state) => state.user);
  
  // Use refs for cleanup and preventing memory leaks
  const intervalRef = useRef(null);
  const abortControllerRef = useRef(null);
  
  // Optimize state structure
  const [prevIsMaintenanceMode, setPrevIsMaintenanceMode] = useState(null);
  const [showPopup, setShowPopup] = useState({
    version: null,
    open: false,
  });

  const refreshCacheAndReload = useCallback(async () => {
    if (process.env.REACT_APP_ENV !== 'production') {
      console.log('Clearing cache and hard reloading...');
    }
    
    try {
      // Clear service worker caches
      if ('caches' in window) {
        const cacheNames = await window.caches.keys();
        await Promise.all(
          cacheNames.map(cacheName => window.caches.delete(cacheName))
        );
      }

      // Clear localStorage safely
      try {
        localStorage.clear();
      } catch (e) {
        console.warn('Failed to clear localStorage:', e);
      }

      // Clear sessionStorage
      try {
        sessionStorage.clear();
      } catch (e) {
        console.warn('Failed to clear sessionStorage:', e);
      }

      // Force reload
      window.location.reload(true);
    } catch (error) {
      console.error('Error during cache refresh:', error);
      // Fallback to simple reload
      window.location.reload(true);
    }
  }, []);

  const checkVersion = useCallback(async () => {
    // Cancel previous request if still pending
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();
    const { signal } = abortControllerRef.current;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/system/dashboard/version`,
        {
          signal,
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const meta = await response.json();
      const currentVersion = process.env.REACT_APP_BUILD_NUMBER;
      const latestVersion = meta?.data;

      if (!latestVersion || !currentVersion) {
        console.warn('Version information is missing');
        return;
      }

      const shouldForceRefresh = parseInt(latestVersion) > parseInt(currentVersion);
      
      if (shouldForceRefresh) {
        if (process.env.REACT_APP_ENV !== 'production') {
          console.log(`New version available: ${latestVersion} (current: ${currentVersion})`);
        }
        setShowPopup({
          version: latestVersion,
          open: true,
        });
      } else if (process.env.REACT_APP_ENV !== 'production') {
        console.log(`Already on latest version: ${latestVersion}`);
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        return; // Request was cancelled, ignore
      }
      console.error('Failed to check version:', error);
    } finally {
      abortControllerRef.current = null;
    }
  }, []);

  const checkMaintenance = useCallback(async () => {
    try {
      const response = await checkIsMaintenanceMode();
      const isMaintenanceActive = response.status === 200;
      
      dispatch(updateIsMaintenanceMode(isMaintenanceActive));
      setPrevIsMaintenanceMode(isMaintenanceActive);
    } catch (error) {
      console.error('Failed to check maintenance mode:', error);
      // On error, assume not in maintenance mode
      dispatch(updateIsMaintenanceMode(false));
      setPrevIsMaintenanceMode(false);
    }
  }, [dispatch]);

  useEffect(() => {
    // Initial checks
    checkMaintenance();
    checkVersion();

    // Set up interval for periodic version checks
    intervalRef.current = setInterval(() => {
      checkVersion();
    }, CHECK_VERSION_INTERVAL);

    // Cleanup function
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, [checkMaintenance, checkVersion]);

  useEffect(() => {
    if (isMaintenanceMode && prevIsMaintenanceMode === false) {
      redirectToDownForMaintenance();
    }
  }, [isMaintenanceMode, prevIsMaintenanceMode]);

  const redirectToDownForMaintenance = useCallback(() => {
    setAppRoute({
      path: ALL_ROUTES.downForMaintenance.path,
    });
  }, [setAppRoute]);

  const closeModal = useCallback(() => {
    setShowPopup(prev => ({
      ...prev,
      version: null,
      open: false,
    }));
  }, []);

  return (
    <>
      <Modal
        open={showPopup.open}
        handleClose={closeModal}
        classes={{ paper: classes.modal, container: classes.modalContainer }}
        title={dialogs.updateRequired}
        secondTitle={dialogs.updateVersionSubtitle}
        secondTitleStyle={classes.modalSecondTitle}
      >
        <IconButton
          aria-label='update'
          className={classes.iconButton}
          onClick={refreshCacheAndReload}
          classes={{ label: classes.label }}
        >
          <UpdateIcon />
          {dialogs.newVersion}
          {showPopup.version}
        </IconButton>
        <Link className={classes.skipNow} onClick={closeModal}>
          {dialogs.skipNow}
        </Link>
      </Modal>
      {children}
    </>
  );
};

export default CacheBuster;
